<template>
  <div data-component-group="filter-forms" data-component="payment">
    <FiltersForm
      :has-filters="hasFilters"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="selectedCountries"
    >
      <v-text-field
        clearable
        v-model="search"
        type="search"
        label="Search"
        prepend-inner-icon="search"
      />

      <v-autocomplete
        clearable
        v-model="contract"
        :items="contracts"
        :loading="isLoadingContracts"
        prepend-inner-icon="article"
        :return-object="true"
        :item-title="(item) => String(item.id)"
        label="Contract ID"
        v-model:search="contractSearch"
      />

      <base-date-picker
        :date="date"
        placeholder="Dates"
        :config="dateConfig"
        :on-change="onChangeDate"
      />

      <v-autocomplete
        clearable
        v-model="status"
        :items="statuses"
        prepend-inner-icon="download_done"
        :return-object="true"
        item-title="name"
        label="Status"
        @focus="onFocusStatus"
      />

      <v-autocomplete
        clearable
        v-model="business"
        :items="businesses"
        :loading="isLoadingBusinesses"
        prepend-inner-icon="domain"
        label="Business"
        item-title="name"
        item-value="id"
        v-model:search="businessSearch"
        @update:model-value="isSelectingBusiness = true"
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import stringEscape from 'js-string-escape';
  import FiltersForm from './FiltersBase';
  import isDateRange from '../../../validators/dateRange';
  import {isNum, isNull, debounce, isStr} from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  const businessSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500,
  });

  // TODO: Translate component
  export default {
    components: {
      FiltersForm,
    },
    emits: ['clearFilters', 'filter'],
    data() {
      return {
        search: '',
        contract: null,
        contractSearch: '',
        business: null,
        businesses: [],
        businessSearch: '',
        isSelectingBusiness: false,
        date: '',
        status: null,
        selectedCountries: [],
        dateConfig: {
          mode: 'range',
        },
        isLoadingContracts: false,
        isLoadingBusinesses: false,
        isRefreshingContracts: false,
      };
    },
    computed: {
      hasFilters() {
        const {
          search,
          contract = {},
          date,
          status = {},
          business,
          selectedCountries = [],
        } = this;

        return (
          search !== '' ||
          isNum(contract?.id) ||
          date !== '' ||
          selectedCountries.length > 0 ||
          isNum(status?.id) ||
          isNum(business)
        );
      },
      contracts() {
        return this.$store.getters['contracts/items'];
      },
      statuses() {
        return this.$store.getters['payments/statuses'];
      },
    },
    watch: {
      contract() {
        this.isRefreshingContracts = true;
      },
      businessSearch(nextSearch) {
        if (this.isSelectingBusiness) {
          this.isSelectingBusiness = false;
          return;
        }
        if (isStr(nextSearch)) {
          businessSearchDebouncer(() => this.getBusinesses(nextSearch));
        }
      },
      contractSearch(nextSearch) {
        if (!isNull(nextSearch)) {
          if (this.isRefreshingContracts) {
            this.isRefreshingContracts = false;
            return false;
          }

          debouncer(() => this.getContracts(nextSearch));
        }
      },
    },
    methods: {
      getBusinesses(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingBusinesses = true;
        return this.$store.dispatch('businesses/getItems', query)
          .then(({ items }) => {
            this.businesses = items;
            this.isLoadingBusinesses = false;
            return items;
          })
          .catch((err) => {
            this.isLoadingBusinesses = false;
            return err;
          });
      },
      getStatuses() {
        return this.$store.dispatch('payments/getStatuses');
      },
      getContracts(search) {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingContracts = true;
        return this.$store
          .dispatch('contracts/getItems', query)
          .then((res) => {
            this.isLoadingContracts = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingContracts = false;
            return err;
          });
      },
      onFilter() {
        const {
          search,
          contract = {},
          date,
          status = {},
          business,
          selectedCountries = [],
        } = this;
        const formattedSearch = isNull(search) ? '' : stringEscape(search);
        const hasDate = date !== '';
        let nextDate = date;

        if (hasDate && !isDateRange(nextDate)) {
          nextDate = `${nextDate} - ${nextDate}`;
        }

        this.$emit('filter', {
          business,
          search: formattedSearch,
          contractId: contract?.id,
          countries: selectedCountries,
          date: nextDate,
          status: status?.id,
        });
      },
      onClearFilters() {
        this.search = '';
        this.contract = null;
        this.selectedCountries = [];
        this.date = '';
        this.status = null;
        this.business = null;

        this.$emit('clearFilters');
      },
      onChangeDate(date) {
        this.date = date;
      },
      onFocusStatus() {
        if (this.statuses.length === 0) {
          this.getStatuses();
        }
      },
    },
  };
</script>
